import { useState, useEffect, useCallback, useRef } from 'react';
import { kitService, setEncargadoKits } from '../../_services/kitService';

export const useKitManagement = (slug, pausarPolling = false) => {
  // Estados principales
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Estado del sistema
  const [sistemaActivado, setSistemaActivado] = useState(false);
  const [esAdministrador, setEsAdministrador] = useState(false);
  const [encargadoActual, setEncargadoActual] = useState(null);
  const [config, setConfig] = useState(null);

  // Rangos
  const [rangos, setRangos] = useState({
    categorias: {},
    categoria_libre: { min: 1, max: 2000 },
  });
  const [editandoRangos, setEditandoRangos] = useState(false);

  // Lista de kits
  const [competitors, setCompetitors] = useState([]);
  const [stats, setStats] = useState({ total: 0, entregados: 0, pendientes: 0 });
  const [filtro, setFiltro] = useState('todos');
  const [searchTerm, setSearchTerm] = useState('');

  // Columnas
  const [columnasTabla, setColumnasTabla] = useState([]);
  const [columnasDinamicas, setColumnasDinamicas] = useState({
    nombre: null,
    apellido: null,
    identificacion: null,
  });

  // Ref para que el polling no interrumpa acciones en curso
  const loadingRef = useRef(false);
  loadingRef.current = loading;

  // Cada vez que cambia el encargado, el service lo envía en el header
  useEffect(() => {
    setEncargadoKits(encargadoActual?.nombre || null);
  }, [encargadoActual]);

  // ==========================================
  // FUNCIONES DE CARGA
  // ==========================================
  const cargarEstadoSistema = useCallback(async () => {
    try {
      const data = await kitService.getEstado(slug);
      setSistemaActivado(data.activado || false);
      setConfig(data);
      if (data.activado && encargadoActual) {
        setEsAdministrador(data.administrador?.nombre === encargadoActual.nombre);
      }
      return data;
    } catch (err) {
      console.error('[KITS] Error cargando estado:', err);
      setError('Error al cargar estado del sistema');
      return null;
    }
  }, [slug, encargadoActual]);

  const cargarRangos = useCallback(async () => {
    try {
      const data = await kitService.getRangos(slug);
      setRangos(data);
      return data;
    } catch (err) {
      console.error('[KITS] Error cargando rangos:', err);
      return null;
    }
  }, [slug]);

  const cargarColumnas = useCallback(async () => {
    try {
      const data = await kitService.getColumnas(slug);
      setColumnasTabla(data.columnas || []);
      return data.columnas || [];
    } catch (err) {
      console.error('[KITS] Error cargando columnas:', err);
      return [];
    }
  }, [slug]);

  const cargarKits = useCallback(
    async ({ silent = false } = {}) => {
      if (!sistemaActivado) return;
      if (!silent) setLoading(true);
      try {
        const data = await kitService.getKits(slug, {
          filtro,
          search: searchTerm,
        });
        setCompetitors(data.data || []);
        setStats(data.stats || { total: 0, entregados: 0, pendientes: 0 });

        if (data.data && data.data.length > 0) {
          const keys = Object.keys(data.data[0] || {});
          const nombreCol = keys.find(
            (k) => /nombre|name|nombres/i.test(k) && !/apellido|apellidos/i.test(k)
          );
          const apellidoCol = keys.find((k) =>
            /apellido|apellidos|lastname/i.test(k)
          );
          const identificacionCol = keys.find((k) =>
            /(^|_)(ci|cedula|dni)|pasaporte|documento|identificacion/i.test(k)
          );
          setColumnasDinamicas((prev) => ({
            nombre: nombreCol || prev.nombre,
            apellido: apellidoCol || prev.apellido,
            identificacion: identificacionCol || prev.identificacion,
          }));
        }
      } catch (err) {
        console.error('[KITS] Error cargando kits:', err);
        if (!silent) setError('Error al cargar lista de kits');
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [slug, sistemaActivado, filtro, searchTerm]
  );

  // ==========================================
  // REGISTRO DE ENCARGADO
  // ==========================================
  const registrarEncargado = useCallback(
    async (nombre, email) => {
      try {
        const data = await kitService.registrarEncargado(slug, { nombre, email });
        setEncargadoActual({ nombre, email, rol: data.data?.rol || 'editor' });
        setEsAdministrador(data.data?.esAdministrador || false);
        return data;
      } catch (err) {
        if (err.response?.status === 404) {
          setEncargadoActual({ nombre, email, rol: 'sin_activar' });
          return { success: true, pendienteActivacion: true };
        }
        console.error('[KITS] Error registrando encargado:', err);
        setError(err.response?.data?.error || 'Error al registrar encargado');
        return null;
      }
    },
    [slug]
  );

  // ==========================================
  // ACTIVACIÓN DEL SISTEMA
  // ==========================================
  const activarSistema = useCallback(
    async (nombre, email) => {
      try {
        setLoading(true);
        const data = await kitService.activarSistema(slug, { nombre, email });
        if (data.success) {
          setSistemaActivado(true);
          setEsAdministrador(true);
          setEncargadoActual({ nombre, email, rol: 'administrador' });
          setConfig(data.data?.config || null);
          setSuccessMessage('✅ Sistema de kits activado correctamente');
          await cargarRangos();
          await cargarColumnas();
          return data;
        }
        return null;
      } catch (err) {
        console.error('[KITS] Error activando sistema:', err);
        setError(err.response?.data?.error || 'Error al activar sistema');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [slug, cargarRangos, cargarColumnas]
  );

  // ==========================================
  // SINCRONIZAR CATEGORÍAS
  // ==========================================
  const sincronizarCategorias = useCallback(async () => {
    try {
      setLoading(true);
      const data = await kitService.sincronizarCategorias(slug);
      if (data.success) {
        setSuccessMessage(`🔄 ${data.message}`);
        if (data.data) setRangos(data.data);
        else await cargarRangos();
        return data;
      }
      return null;
    } catch (err) {
      console.error('[KITS] Error sincronizando categorías:', err);
      setError(err.response?.data?.error || 'Error al sincronizar categorías');
      return null;
    } finally {
      setLoading(false);
    }
  }, [slug, cargarRangos]);

  // ==========================================
  // ACCIONES DE DORSALES
  // ==========================================
  const asignarDorsalesAutomaticos = useCallback(async () => {
    if (
      !window.confirm(
        '⚠️ ¿Asignar dorsales automáticamente según categorías? Esta acción es permanente.'
      )
    ) {
      return null;
    }
    setLoading(true);
    try {
      const data = await kitService.asignarDorsalesAutomaticos(slug);
      if (data.success) {
        setSuccessMessage(`✅ ${data.message}`);
        await cargarKits();
        await cargarRangos();
      }
      return data;
    } catch (err) {
      console.error('[KITS] Error asignando dorsales:', err);
      setError(err.response?.data?.error || 'Error al asignar dorsales');
      return null;
    } finally {
      setLoading(false);
    }
  }, [slug, cargarKits, cargarRangos]);

  const asignarDorsalManual = useCallback(
    async (id, dorsal, forzar = false) => {
      setLoading(true);
      try {
        const data = await kitService.asignarDorsalManual(slug, id, {
          dorsal,
          forzar,
        });
        if (data.success) {
          if (data.data?.advertencia) {
            setSuccessMessage(
              `⚠️ Dorsal ${dorsal} asignado, pero está fuera del rango de ${data.data.advertencia.categoria} (${data.data.advertencia.rango.min}-${data.data.advertencia.rango.max})`
            );
          } else {
            setSuccessMessage(`✅ Dorsal ${dorsal} asignado correctamente`);
          }
          await cargarKits();
          return data;
        }
        return null;
      } catch (err) {
        console.error('[KITS] Error asignando dorsal manual:', err);
        if (err.response?.status === 409) {
          const yaAsignado = err.response?.data?.ya_asignado;
          if (
            yaAsignado &&
            !forzar &&
            window.confirm(
              `⚠️ El dorsal ${dorsal} ya está asignado a ${yaAsignado.nombre}. ¿Asignar de todas formas?`
            )
          ) {
            setLoading(false);
            return await asignarDorsalManual(id, dorsal, true);
          }
          if (!yaAsignado) {
            setError(
              err.response?.data?.error ||
                'Otro encargado asignó ese dorsal hace un momento'
            );
          }
          await cargarKits();
        } else {
          setError(err.response?.data?.error || 'Error al asignar dorsal');
        }
        return null;
      } finally {
        setLoading(false);
      }
    },
    [slug, cargarKits]
  );

  // ==========================================
  // EDICIÓN DE DATOS
  // ==========================================
  const actualizarDatosCorredor = useCallback(
    async (id, datos) => {
      try {
        const data = await kitService.actualizarDatosCorredor(slug, id, datos);
        if (data.success && data.actualizado) {
          setSuccessMessage('✅ Datos del corredor actualizados');
          await cargarKits();
        }
        return data;
      } catch (err) {
        console.error('[KITS] Error actualizando datos:', err);
        setError(err.response?.data?.error || 'Error al actualizar datos');
        return null;
      }
    },
    [slug, cargarKits]
  );

  const guardarCampo = useCallback(
    async (id, campo, valor) => {
      try {
        const data = await kitService.actualizarDatosCorredor(slug, id, {
          [campo]: valor,
        });
        if (data.success && data.actualizado) {
          setCompetitors((prev) =>
            prev.map((c) => (c.id === id ? { ...c, [campo]: valor } : c))
          );
          setSuccessMessage(`✅ ${campo} actualizado`);
        }
        return data;
      } catch (err) {
        console.error('[KITS] Error guardando campo:', err);
        setError(err.response?.data?.error || 'Error al guardar el cambio');
        return null;
      }
    },
    [slug]
  );

  // ==========================================
  // ACCIONES DE RANGOS
  // ==========================================
  const actualizarRangos = useCallback(
    async (nuevosRangos) => {
      setLoading(true);
      try {
        const data = await kitService.actualizarRangos(slug, nuevosRangos);
        if (data.success) {
          setRangos(data.data);
          setSuccessMessage('✅ Rangos actualizados correctamente');
          setEditandoRangos(false);
          return data;
        }
        return null;
      } catch (err) {
        console.error('[KITS] Error actualizando rangos:', err);
        setError(err.response?.data?.error || 'Error al actualizar rangos');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [slug]
  );

  // ==========================================
  // ACCIONES DE ENTREGA
  // ==========================================
  const entregarKit = useCallback(
    async (id, payload) => {
      setLoading(true);
      try {
        const response = await kitService.entregarKit(slug, id, payload);
        if (response.success) {
          setSuccessMessage(`✅ ${response.message}`);
          await cargarKits();
          return response;
        }
        return null;
      } catch (err) {
        console.error('[KITS] Error entregando kit:', err);
        if (err.response?.status === 409) {
          const yaEntregado = err.response?.data?.ya_entregado;
          const fecha = yaEntregado?.fecha_entrega
            ? new Date(yaEntregado.fecha_entrega).toLocaleString()
            : 'hace un momento';
          setError(
            `⚠️ Este kit ya fue entregado por ${
              yaEntregado?.entregado_por || 'otro encargado'
            } (${fecha})`
          );
          await cargarKits();
        } else {
          setError(err.response?.data?.error || 'Error al entregar kit');
        }
        return null;
      } finally {
        setLoading(false);
      }
    },
    [slug, cargarKits]
  );

  const revertirEntrega = useCallback(
    async (id) => {
      const motivo = window.prompt('📝 Motivo de la reversión:');
      if (motivo === null) return null;
      if (!window.confirm('⚠️ ¿Estás seguro de revertir la entrega de este kit?')) {
        return null;
      }
      setLoading(true);
      try {
        const data = await kitService.revertirEntrega(slug, id, { motivo });
        if (data.success) {
          setSuccessMessage('🔄 Entrega revertida correctamente');
          await cargarKits();
          return data;
        }
        return null;
      } catch (err) {
        console.error('[KITS] Error revirtiendo entrega:', err);
        setError(err.response?.data?.error || 'Error al revertir entrega');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [slug, cargarKits]
  );

  // ==========================================
  // EFECTOS
  // ==========================================
  // 1) Carga inicial
  useEffect(() => {
    if (!slug) return;
    const init = async () => {
      const estado = await cargarEstadoSistema();
      if (estado?.activado) {
        await cargarColumnas();
        await cargarRangos();
        await cargarKits();
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // 2) Cargar kits con debounce cuando cambian filtro / búsqueda
  useEffect(() => {
    if (!sistemaActivado) return;
    const t = setTimeout(() => {
      cargarKits();
    }, searchTerm ? 400 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtro, searchTerm, sistemaActivado]);

  // 3) 🔄 POLLING cada 3 segundos (pausado si pausarPolling = true)
  useEffect(() => {
    if (!sistemaActivado) return;
    if (pausarPolling) return;
    const interval = setInterval(() => {
      if (document.hidden) return;
      if (loadingRef.current) return;
      cargarKits({ silent: true });
    }, 3000);
    return () => clearInterval(interval);
  }, [sistemaActivado, pausarPolling, cargarKits]);

  // 4) Auto-limpiar mensajes de éxito (5s)
  useEffect(() => {
    if (!successMessage) return;
    const t = setTimeout(() => setSuccessMessage(null), 5000);
    return () => clearTimeout(t);
  }, [successMessage]);

  // 5) Auto-limpiar mensajes de error (6s)
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(null), 6000);
    return () => clearTimeout(t);
  }, [error]);

  // ==========================================
  // RETORNAR
  // ==========================================
  return {
    // Estado
    loading,
    error,
    successMessage,
    sistemaActivado,
    esAdministrador,
    encargadoActual,
    config,
    rangos,
    editandoRangos,
    competitors,
    stats,
    filtro,
    searchTerm,
    columnasDinamicas,
    columnasTabla,
    // Setters
    setError,
    setSuccessMessage,
    setEditandoRangos,
    setFiltro,
    setSearchTerm,
    setEncargadoActual,
    // Acciones
    cargarEstadoSistema,
    cargarRangos,
    cargarKits,
    cargarColumnas,
    registrarEncargado,
    activarSistema,
    sincronizarCategorias,
    asignarDorsalesAutomaticos,
    asignarDorsalManual,
    actualizarRangos,
    entregarKit,
    revertirEntrega,
    actualizarDatosCorredor,
    guardarCampo,
  };
};